import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ScreenContent,
  Title,
  Success,
  Button,
  StyledTitle,
  Content,
  Text,
  ButtonContainer,
  StyledButton,
  SubTitle,
  Link,
  ErrorDiv,
  StyledContent,
} from "./CheckoutFinalPage.styles";
import Loader from "../../../components/Loader/Loader";
import SuccessImg from "../../../assets/images/success.png";
import FailedImg from "../../../assets/images/failed.png";
import { toast } from "react-toastify";
import { fetchBusinessUserInfo } from "../../../store/slices/businessUser/asyncThunks";
import { selectBusinessUserInfo } from "../../../store/slices/businessUser/slice";
import Header from "../../../components/NewHeader/NewHeader";
import Footer from "../../../components/Footer";
import marketplaceService from "../../../api/services/marketplace";
import { resetMarketplaceOrderData } from "../../../store/slices/marketplace/marketplaceSlice";
import { HeaderWrapper, MainContainer } from "./MarketplacePage.styles";

const CheckoutFinalPage = () => {
  const { t } = useTranslation();
  const navigation = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { state } = location;
  const businessUser = useSelector(selectBusinessUserInfo);
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);

  const queryParams = new URLSearchParams(location.search);
  const subscriptionId = queryParams.get("subscriptionId");

  // const searchParams = useSearchParams();
  // const paymentChargeId = searchParams.get("tap_id");
  // const bankTransfer = searchParams.get("bank_transfer");
  // const searchParams = useSearchParams();
  const [searchParams] = useSearchParams();

  const paymentChargeId = searchParams?.get("tap_id");
  const bankTransfer = searchParams?.get("bank_transfer");

  const [error, setError] = useState(false);

  useEffect(() => {
    dispatch(resetMarketplaceOrderData());
    if (state) {
      if (state.order) {
        setOrder(state?.order);
      }
    }
    if (paymentChargeId) {
      setLoading(true);
      marketplaceService
        .validatePayment({ paymentChargeId })
        .then(() => {
          setLoading(false);
          setError(false);
        })
        .catch((err) => {
          toast.error(t("Payment validation failed"));
          setError(true);
          setLoading(false);
        });
    }
  }, [
    businessUser.company.VAT,
    businessUser.company.address,
    businessUser.company.city,
    businessUser.company.name,
    businessUser.company.zipCode,
    dispatch,
    paymentChargeId,
    state,
    subscriptionId,
  ]);

  const downloadInvoice = (value) => {
    if (value?.invoiceUrl) {
      window.open(value?.invoiceUrl, "_blank");
    }
  };

  const [headerVisible, setHeaderVisible] = useState(true);
  const [lastScrollUpY, setLastScrollUpY] = useState(0);
  const [scrollDirection, setScrollDirection] = useState(null);
  const lastScrollTopRef = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollTop = window.scrollY;
      const lastScrollTop = lastScrollTopRef.current;

      if (currentScrollTop === 0) {
        setHeaderVisible(true);
        setLastScrollUpY(currentScrollTop);
        setScrollDirection(null);
        lastScrollTopRef.current = currentScrollTop;
        return;
      }

      if (currentScrollTop > lastScrollTop) {
        setScrollDirection("down");
        setLastScrollUpY(currentScrollTop);
      } else {
        setScrollDirection("up");
      }

      if (scrollDirection === "up" && currentScrollTop < lastScrollUpY - 350) {
        setHeaderVisible(true);
        setLastScrollUpY(currentScrollTop);
      }

      if (
        scrollDirection === "down" &&
        currentScrollTop > lastScrollUpY + 350
      ) {
        setHeaderVisible(false);
      }

      lastScrollTopRef.current = currentScrollTop;
    };

    const handleScrollDebounced = () => {
      requestAnimationFrame(handleScroll);
    };

    window.addEventListener("scroll", handleScrollDebounced);

    return () => {
      window.removeEventListener("scroll", handleScrollDebounced);
    };
  }, [scrollDirection]);

  return (
    <MainContainer>
      {loading && <Loader disableScroll />}

      {!order && (
        <HeaderWrapper
          style={{
            opacity: headerVisible ? 1 : 0,
            marginTop: headerVisible ? 0 : -150,
            transition: "all 0.3s ease-in-out",
          }}
        >
          <Header />
        </HeaderWrapper>
      )}

      <StyledContent>
        <ScreenContent>
          <StyledTitle>
            {!order ? t("checkout") : t("download-invoice")}
          </StyledTitle>
          <Content>
            {!order ? (
              error ? (
                <Success src={FailedImg} alt="success-img" />
              ) : (
                <Success src={SuccessImg} alt="success-img" />
              )
            ) : null}
            {error ? (
              <ErrorDiv>
                <Title>{t("payment-failed")}</Title>
                <Text>
                  {t("contact-us-at")}{" "}
                  <Link href={"mailto:hello@ygii.app"}>hello@ygii.app</Link>
                </Text>
                {!businessUser.loggedIn && (
                  <SubTitle>
                    {t("check-your-email-and-verify-account")}
                  </SubTitle>
                )}
              </ErrorDiv>
            ) : (
              <>
                {bankTransfer ? (
                  <>
                    <Title>{t("our-admin-will-check-your-invoice")}</Title>
                    {!businessUser.loggedIn && (
                      <SubTitle>
                        {t("check-your-email-and-verify-account")}
                      </SubTitle>
                    )}
                  </>
                ) : (
                  <>
                    {!order ? <Title>{t("payment-completed")}</Title> : null}
                    {!businessUser.loggedIn && (
                      <SubTitle>
                        {t("check-your-email-and-verify-account")}
                      </SubTitle>
                    )}
                  </>
                )}
              </>
            )}
          </Content>
          <ButtonContainer>
            {!error && (
              <>
                {!bankTransfer && order && (
                  <StyledButton
                    onClick={() => {
                      downloadInvoice(order);
                    }}
                  >
                    {t("download-invoice")}
                  </StyledButton>
                )}
              </>
            )}
            {!businessUser.loggedIn ? (
              <Button
                onClick={() => {
                  navigation("/marketplace");
                }}
              >
                {t("go-to-marketplace")}
              </Button>
            ) : (
              <Button
                onClick={() => {
                  dispatch(fetchBusinessUserInfo());
                  if (!order && !subscriptionId) {
                    navigation(
                      "/admin-portal/order-history?marketplacePage=1&giftsPage=1"
                    );
                  } else if (
                    businessUser.role === "superAdmin" &&
                    !subscriptionId
                  ) {
                    navigation(
                      `/admin-portal/order-history/marketplace-order-details/${order?.id}`
                    );
                  } else if (businessUser.role === "owner" && !subscriptionId) {
                    navigation(
                      "/admin-portal/order-history?marketplacePage=1&giftsPage=1"
                    );
                  } else if (
                    subscriptionId &&
                    !state?.subscriptionCompany &&
                    businessUser.role !== "superAdmin" &&
                    !order
                  ) {
                    navigation(`/admin-portal/business-subscriptions?page=1`);
                  } else if (
                    subscriptionId &&
                    state?.subscriptionCompany &&
                    businessUser.role === "superAdmin" &&
                    !order
                  ) {
                    navigation(
                      `/admin-portal/subscriptions/company-history/${state.subscriptionCompany.id}?page=1`
                    );
                  }
                }}
              >
                {order || (subscriptionId && !order)
                  ? t("back")
                  : t("go-to-dashboard")}
              </Button>
            )}
          </ButtonContainer>
        </ScreenContent>
      </StyledContent>
      <Footer />
    </MainContainer>
  );
};

export default CheckoutFinalPage;
